import { initialSenderMailState, senderMailChoices, senderMailState } from "./senderMailState";
import { useEffect, useReducer, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  SenderMailMessage,
  SenderMailPreview,
  SenderSpamResult,
  SenderView,
  WorkView,
} from "@shared/api";
import { api, isApiError } from "@web/lib/api";
import { senderUrls } from "@web/lib/sendersApi";
import { Modal } from "@web/ui/Modal";
import { Button } from "@web/ui/Button";
import { formatFull } from "@web/lib/time";

export function SenderMailDialog({
  sender,
  initialSpam,
  onClose,
}: {
  sender: SenderView;
  initialSpam: boolean;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const base = `${senderUrls.sender(sender.accountId, sender.address)}/messages`;
  const [state, dispatch] = useReducer(senderMailState, initialSenderMailState);
  const [armOnLoad, setArmOnLoad] = useState(initialSpam);
  const confirming = !!state.approval;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const workId = state.submission?.workId ?? null;
  const list = useQuery({
    queryKey: ["sender-mail", sender.accountId, sender.address],
    queryFn: ({ signal }) =>
      api<{ messages: SenderMailMessage[]; hasMore: boolean }>(base, { signal }),
  });
  useEffect(() => {
    if (list.data) dispatch({ type: "loaded", messages: list.data.messages });
  }, [list.data]);
  const items = senderMailChoices(state, list.data?.messages ?? []);
  const key = state.selected?.key;
  const preview = useQuery({
    queryKey: ["sender-preview", base, key],
    queryFn: ({ signal }) =>
      api<SenderMailPreview>(`${base}/${encodeURIComponent(key!)}`, { signal }),
    enabled: !!key && !state.submission,
    staleTime: 0,
  });
  useEffect(() => {
    if (armOnLoad && preview.data && preview.data.key === key && !preview.isFetching) {
      dispatch({ type: "arm", message: preview.data });
      setArmOnLoad(false);
    }
  }, [armOnLoad, preview.data, preview.isFetching, key]);
  const m = state.submission?.message ?? state.approval ?? preview.data;
  const work = useQuery({
    queryKey: ["sender-spam-work", workId],
    queryFn: ({ signal }) => api<WorkView>(`/api/work/${encodeURIComponent(workId!)}`, { signal }),
    enabled: !!workId,
    refetchInterval: (q) =>
      q.state.data &&
      ["completed", "failed", "blocked", "cancelled", "stale"].includes(q.state.data.status)
        ? false
        : 1500,
  });
  const waiting = busy || !!workId;

  const confirm = async () => {
    const reviewed = state.approval;
    if (!reviewed?.spamFingerprint || reviewed.key !== key || waiting || preview.isFetching) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api<SenderSpamResult>(`${base}/${encodeURIComponent(reviewed.key)}/spam`, {
        method: "POST",
        body: { fingerprint: reviewed.spamFingerprint },
      });
      dispatch({ type: "submitted", message: reviewed, workId: r.work.id });
    } catch (e) {
      setError(
        isApiError(e) && e.serverMessage
          ? e.serverMessage
          : "Couldn't confirm whether the report was queued. Check Under way before trying again.",
      );
      // A stale or failed request must be reviewed again before another confirmation.
      dispatch({ type: "cancel" });
      void preview.refetch();
    } finally {
      setBusy(false);
    }
  };
  const next = () => {
    dispatch({ type: "next", messages: list.data?.messages ?? [] });
    setError(null);
    void client.invalidateQueries({ queryKey: ["senders"] });
    void client.invalidateQueries({ queryKey: ["mail"] });
    void client.invalidateQueries({ queryKey: ["board"] });
    toast.success("Moved to Gmail Spam");
  };
  const done = work.data?.status === "completed";
  useEffect(() => {
    if (!done) return;
    void client.invalidateQueries({ queryKey: ["senders"] });
    void client.invalidateQueries({ queryKey: ["mail"] });
    void client.invalidateQueries({ queryKey: ["board"] });
  }, [done, client]);
  const stopped =
    work.data && ["failed", "blocked", "cancelled", "stale"].includes(work.data.status);
  return (
    <Modal
      open
      onClose={onClose}
      label="Preview sender mail"
      className="w-[min(44rem,calc(100vw-2rem))]"
    >
      <div className="flex max-h-[85dvh] flex-col">
        <div className="flex items-start justify-between gap-4 border-b border-line p-5">
          <div className="min-w-0">
            <h2 className="font-serif text-xl">
              {done
                ? "Moved to Gmail Spam"
                : confirming
                  ? "Move this message to Gmail Spam?"
                  : "Preview"}
            </h2>
            <p className="mt-1 break-all text-sm text-muted">{sender.address}</p>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
        <div className="space-y-4 overflow-y-auto p-5">
          <p className="rounded-lg bg-sunk p-3 text-sm text-muted">
            Text only. Images, tracking pixels, attachments and email links are not loaded.
            Previewing does not mark the email read in Gmail.
          </p>
          {list.isPending && !m ? (
            <p role="status">Loading messages…</p>
          ) : list.isError && !m ? (
            <p role="alert">
              Couldn't load messages.{" "}
              <button className="underline" onClick={() => void list.refetch()}>
                Try again
              </button>
            </p>
          ) : !items.length ? (
            <p>No more messages available here. Reported messages are in Gmail’s Spam folder.</p>
          ) : (
            <>
              {items.length > 1 ? (
                <label className="block text-sm">
                  Message
                  <select
                    className="mt-1 block w-full rounded border border-line bg-paper p-2"
                    value={key ?? ""}
                    disabled={waiting}
                    onChange={(e) => {
                      const message = items.find((item) => item.key === e.target.value);
                      if (message) dispatch({ type: "select", message });
                      setArmOnLoad(false);
                      setError(null);
                    }}
                  >
                    {items.map((item) => (
                      <option key={item.key} value={item.key}>
                        {item.subject} — {formatFull(item.receivedAt)}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              {list.data?.hasMore ? (
                <p className="text-sm text-muted">
                  Showing the most recent 25 cached messages from this sender.
                </p>
              ) : null}
              {!m && (preview.isPending || !key) ? (
                <p role="status">Loading preview…</p>
              ) : !m ? (
                <p role="alert">
                  Couldn't load the preview.{" "}
                  <button className="underline" onClick={() => void preview.refetch()}>
                    Try again
                  </button>
                </p>
              ) : (
                <>
                  <dl className="space-y-1 text-sm">
                    <div>
                      <dt className="inline font-medium">Mailbox: </dt>
                      <dd className="inline break-all">{m.accountEmail}</dd>
                    </div>
                    <div>
                      <dt className="inline font-medium">From: </dt>
                      <dd className="inline break-all">{m.sender}</dd>
                    </div>
                    <div>
                      <dt className="inline font-medium">Subject: </dt>
                      <dd className="inline">{m.subject}</dd>
                    </div>
                    <div>
                      <dt className="inline font-medium">Received: </dt>
                      <dd className="inline">{formatFull(m.receivedAt)}</dd>
                    </div>
                  </dl>
                  {confirming && !workId ? (
                    <div className="rounded-lg border border-line-strong bg-sunk p-3 text-sm">
                      <p>
                        Only this message will move to Gmail Spam and leave the inbox. Other
                        messages and future mail from this sender are unchanged by MeeMail.
                      </p>
                      <p className="mt-2">
                        Google may analyze reported mail to improve spam detection. Gmail
                        automatically deletes Spam after 30 days. You can use “Not spam” in Gmail
                        before then.
                      </p>
                    </div>
                  ) : null}
                  {!m.spamFingerprint && !workId ? (
                    <p className="text-sm text-muted">
                      Spam is unavailable for this message. Check it in Gmail.
                    </p>
                  ) : null}
                  {!m.bodyAvailable ? (
                    <p className="text-sm text-muted">
                      Full body unavailable; this is the cached snippet.
                    </p>
                  ) : null}
                  <div
                    className="whitespace-pre-wrap break-words rounded-lg border border-line bg-paper p-4 text-[15px] leading-relaxed"
                    data-testid="sender-preview-text"
                  >
                    {m.text || "(Empty message)"}
                  </div>
                  {m.truncated ? (
                    <p className="text-sm text-muted">
                      Long message; preview stops after 50,000 characters.
                    </p>
                  ) : null}
                </>
              )}
            </>
          )}
          {error ? (
            <p role="alert" className="text-sm">
              {error}
            </p>
          ) : null}
          {workId ? (
            <p role="status" className="text-sm">
              {done
                ? "Gmail confirmed: this message is in Spam."
                : stopped
                  ? (work.data?.resultText ??
                    "The report didn't finish. Check Under way for details.")
                  : work.data?.phase === "main_unknown"
                    ? "Checking Gmail’s response. MeeMail will not repeat the report automatically."
                    : work.isError
                      ? "Couldn't check progress. The report may still be running; check Under way before trying again."
                      : "Waiting for Gmail to confirm…"}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap justify-end gap-2 border-t border-line p-4">
          {done ? (
            <Button onClick={next}>Next message</Button>
          ) : workId ? (
            <Button onClick={onClose}>Close — check Under way</Button>
          ) : confirming ? (
            <>
              <Button onClick={() => dispatch({ type: "cancel" })} disabled={busy}>
                Cancel
              </Button>
              <Button
                variant="quiet-danger"
                busy={busy}
                disabled={
                  !m?.spamFingerprint || preview.isFetching || preview.isError || list.isError
                }
                onClick={() => void confirm()}
              >
                Confirm spam
              </Button>
            </>
          ) : (
            <>
              <Button onClick={onClose}>Done</Button>
              <Button
                variant="quiet-danger"
                disabled={
                  !m?.spamFingerprint || preview.isFetching || preview.isError || !items.length
                }
                onClick={() => {
                  if (m) dispatch({ type: "arm", message: m });
                }}
              >
                Spam…
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
